// FE-SCRM-056 — Computed-due-date helper for the apply preview.
//
// Mirrors the BE's ``apply_activity_playbook`` due-date calculation
// in ``backend/sales/playbook_service.py``:
//
//   anchor   = date(opportunity.renewal_year, renewal_month, 1)
//   due_date = anchor + timedelta(days=block.offset_days_from_renewal)
//
// Negative offsets are pre-renewal; positive are post-renewal. The
// BE composes ``due_at`` as midnight UTC; the FE preview only needs
// the calendar date so we format from the date components directly
// (no Date() construction — that introduces a local-tz pitfall on
// the very first day of the month).


/** Returns ``YYYY-MM-DD`` (calendar date in UTC) for ``ordinal`` =
 * anchor (renewal_year-renewal_month-01) plus ``offsetDays``. Returns
 * ``null`` if either anchor component is missing.
 */
export function computeDueDate(
  renewalYear: number | null,
  renewalMonth: number | null,
  offsetDays: number,
): string | null {
  if (renewalYear === null || renewalMonth === null) return null;
  // ``Date.UTC`` is tz-safe — it interprets its args as UTC, then
  // adding/subtracting epoch-ms keeps us in UTC. We round to whole
  // days and read ISO components for the calendar date.
  const anchorMs = Date.UTC(renewalYear, renewalMonth - 1, 1);
  const dueMs = anchorMs + offsetDays * 86_400_000;
  const due = new Date(dueMs);
  const y = due.getUTCFullYear();
  const m = String(due.getUTCMonth() + 1).padStart(2, "0");
  const d = String(due.getUTCDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}


/** "12 weeks pre-renewal" / "2 weeks post-renewal" / "On renewal" copy
 * derived from the playbook's MIN offset (most-negative). Used in the
 * playbook-row summary "duration window" column. Returns an empty
 * string if ``offsets`` is empty.
 */
export function durationWindowLabel(offsets: number[]): string {
  if (offsets.length === 0) return "";
  const min = Math.min(...offsets);
  if (min === 0) return "On renewal";
  const weeks = Math.round(Math.abs(min) / 7);
  if (min < 0) {
    return weeks === 1 ? "1 week pre-renewal" : `${weeks} weeks pre-renewal`;
  }
  return weeks === 1 ? "1 week post-renewal" : `${weeks} weeks post-renewal`;
}
